import { useMemo, useState } from 'react';
import { formatToLocal } from '@safetyhub/shared';
import { Badge, Button, Card, CARD_SECTION_TITLE_CLASS, ConfirmModal, TextButton } from '@safetyhub/ui';
import { MODAL_LAYER } from '../../../components/modalLayers';
import { useToastStore } from '../../../stores/toast';
import { rbacApi, type CompanyRoleView, type CreateRoleAssignmentDto, type RoleAssignmentView } from '../../../lib/rbac';
import { AssignRoleModal } from './AssignRoleModal';
import { rbacErrorMessage } from './rbacErrors';

function formatAssignedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return formatToLocal(date, { format: { year: 'numeric', month: 'numeric', day: 'numeric' } });
}

/**
 * 역할 정렬 — 법정 직위(legalPosition 있는 역할)가 먼저, 그 안에서는 서버 `sortOrder`. 소유자 역할은
 * 배정 대상이 아니라(409 `ROLE_ASSIGNMENT_OWNER_FORBIDDEN` — 소유자는 멤버 관리·초대에서) 목록에서 뺀다.
 */
export function orderRolesForAssignment(roles: CompanyRoleView[]): CompanyRoleView[] {
  return roles
    .filter((r) => !r.isOwner)
    .slice()
    .sort((a, b) => {
      const la = a.legalPosition ? 0 : 1;
      const lb = b.legalPosition ? 0 : 1;
      if (la !== lb) return la - lb;
      return a.sortOrder - b.sortOrder;
    });
}

interface AssignmentsSectionProps {
  companyId: string;
  roles: CompanyRoleView[];
  assignments: RoleAssignmentView[];
  isMobile: boolean;
  onChanged: () => Promise<void>;
}

/**
 * ② 직위별 배정 — 역할마다 보유자 목록(이름·연락처·선임일·위탁 배지) + 「배정」/행별 「해제」.
 * `singleHolder` 역할에 보유자가 있으면 배정 버튼을 잠그고 사유를 적는다(서버 409 `ROLE_ASSIGNMENT_SINGLE_HOLDER_TAKEN`을
 * 화면에서 먼저). 409/400 코드 → 문구는 `rbacErrors.ts`.
 */
export function AssignmentsSection({ companyId, roles, assignments, isMobile, onChanged }: AssignmentsSectionProps) {
  const { showToast } = useToastStore();
  const [assignTarget, setAssignTarget] = useState<CompanyRoleView | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [unassignTarget, setUnassignTarget] = useState<RoleAssignmentView | null>(null);
  const [isUnassigning, setIsUnassigning] = useState(false);

  const ordered = useMemo(() => orderRolesForAssignment(roles), [roles]);
  const byRole = useMemo(() => {
    const map = new Map<string, RoleAssignmentView[]>();
    for (const a of assignments) {
      const list = map.get(a.roleKey) ?? [];
      list.push(a);
      map.set(a.roleKey, list);
    }
    return map;
  }, [assignments]);

  const handleAssign = async (payload: CreateRoleAssignmentDto) => {
    setIsAssigning(true);
    try {
      await rbacApi.createAssignment(companyId, payload);
      showToast('배정했습니다.', 'success');
      setAssignTarget(null);
      await onChanged();
    } catch (err) {
      showToast(rbacErrorMessage(err, '배정에 실패했습니다.'), 'error');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassign = async () => {
    if (!unassignTarget) return;
    setIsUnassigning(true);
    try {
      await rbacApi.deleteAssignment(companyId, unassignTarget.id);
      showToast('해제했습니다.', 'success');
      setUnassignTarget(null);
      await onChanged();
    } catch (err) {
      showToast(rbacErrorMessage(err, '해제에 실패했습니다.'), 'error');
    } finally {
      setIsUnassigning(false);
    }
  };

  return (
    <Card padding="xl" radius="lg" elevation="md">
      <h2 className={`${CARD_SECTION_TITLE_CLASS} mb-1`}>직위별 배정</h2>
      <p className="text-xs text-ink-500 mb-4">
        법정 직위와 조직 역할을 맡은 인원입니다. 겸임은 역할마다 따로 배정합니다.
      </p>

      <ul className="divide-y divide-line-soft">
        {ordered.map((role) => {
          const holders = byRole.get(role.key) ?? [];
          const singleTaken = role.singleHolder && holders.length > 0;
          return (
            <li key={role.key} className="py-4 first:pt-0 last:pb-0" data-role-key={role.key}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-ink-900">{role.name}</span>
                    {role.legalPosition && (
                      <Badge tone="brand" shape="rounded" size="sm">
                        법정 직위
                      </Badge>
                    )}
                    {role.singleHolder && (
                      <Badge tone="slate" shape="rounded" size="sm">
                        1인
                      </Badge>
                    )}
                    {role.scope === 'COMPANY' && (
                      <Badge tone="violet" shape="rounded" size="sm">
                        조직 역할
                      </Badge>
                    )}
                  </div>
                  {role.description && <p className="mt-0.5 text-xs text-ink-500">{role.description}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    onClick={() => setAssignTarget(role)}
                    disabled={singleTaken}
                    title={singleTaken ? '한 명만 맡을 수 있는 직위입니다' : undefined}
                  >
                    배정
                  </Button>
                  {singleTaken && <span className="text-[11px] text-ink-400">한 명만 맡을 수 있습니다</span>}
                </div>
              </div>

              {holders.length === 0 ? (
                <p className="mt-2 text-xs text-ink-400">배정된 인원이 없습니다.</p>
              ) : (
                <ul className="mt-2 space-y-1.5">
                  {holders.map((h) => (
                    <li
                      key={h.id}
                      className={`flex ${isMobile ? 'flex-col gap-1' : 'items-center justify-between gap-3'} rounded-card bg-surface-soft px-3 py-2`}
                    >
                      <div className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                        <span className="font-medium text-ink-900">{h.member.name}</span>
                        {!h.member.hasAccount && (
                          <Badge tone="amber" shape="rounded" size="sm">
                            계정 없음
                          </Badge>
                        )}
                        {h.delegatedToAgency && (
                          <Badge tone="teal" shape="rounded" size="sm">
                            위탁{h.agencyName ? ` · ${h.agencyName}` : ''}
                          </Badge>
                        )}
                        <span className="text-xs text-ink-500">{h.member.phone ?? h.member.email ?? '-'}</span>
                        <span className="text-xs text-ink-500">선임일 {formatAssignedAt(h.assignedAt)}</span>
                        {h.note && <span className="text-xs text-ink-400">{h.note}</span>}
                      </div>
                      <TextButton
                        tone="danger"
                        type="button"
                        onClick={() => setUnassignTarget(h)}
                        className="text-sm px-2 py-1 hover:bg-rose-50 self-end"
                      >
                        해제
                      </TextButton>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      <AssignRoleModal
        open={assignTarget !== null}
        role={assignTarget}
        companyId={companyId}
        currentHolders={assignTarget ? (byRole.get(assignTarget.key) ?? []) : []}
        isSubmitting={isAssigning}
        overlayZClassName={MODAL_LAYER.root}
        onSubmit={handleAssign}
        onClose={() => {
          if (!isAssigning) setAssignTarget(null);
        }}
      />

      <ConfirmModal
        open={unassignTarget !== null}
        title="배정 해제"
        body={
          unassignTarget
            ? `${unassignTarget.member.name}님의 「${unassignTarget.roleName}」 배정을 해제합니다. 이 역할로 받던 권한이 사라집니다.`
            : undefined
        }
        confirmLabel="해제"
        danger
        busy={isUnassigning}
        overlayZClassName={MODAL_LAYER.root}
        onConfirm={handleUnassign}
        onClose={() => {
          if (!isUnassigning) setUnassignTarget(null);
        }}
      />
    </Card>
  );
}
